import { LoadingSkeleton } from '../../shared/ui/loading';
import { SessionQueryProvider } from '../../shared/query/SessionQueryProvider';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { configureAuthTransport } from '../../shared/api/http';
import { getAuthStatus, getCurrentSession, setupAdmin, logIn, logOut } from './api';
import { validateNewPassword } from './password';
import { FormErrorSummary } from './FormErrorSummary';
import {
  USERNAME_PATTERN,
  operationIssue,
  passwordIssue,
  firstConstraintIssue,
  errorDescription,
  type FormIssue,
} from './form-feedback';
import type { AuthSessionResDto, UserResDto } from './dto';
import {
  PRODUCT_BRAND,
  PRODUCT_WORDMARK,
  PRODUCT_REGION,
  PRODUCT_DESCRIPTION,
} from '../../shared/product';

type AuthContextValue = {
  user: UserResDto | null;
  logout: () => Promise<void>;
  endSession: (message?: string) => void;
};
const AuthContext = createContext<AuthContextValue | null>(null);
export function useSession() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('Session provider required');
  return auth;
}
export function useAuth() {
  const session = useSession();
  if (!session.user) throw new Error('Authentication required');
  return { ...session, user: session.user };
}
export function AuthProvider({
  children,
  requireLogin = true,
  loadingFallback,
}: {
  children: ReactNode;
  requireLogin?: boolean;
  loadingFallback?: ReactNode;
}) {
  const [session, setSession] = useState<AuthSessionResDto | null>(null);
  const [status, setStatus] = useState<'loading' | 'login' | 'setup' | 'ready'>('loading');
  const [error, setError] = useState<FormIssue | null>(null);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    if (requireLogin && status !== 'ready') {
      document.title = `${status === 'setup' ? 'Thiết lập hệ thống' : 'Đăng nhập'} · ${PRODUCT_BRAND}`;
    }
  }, [status, requireLogin]);
  useLayoutEffect(() => {
    configureAuthTransport(
      () => session?.csrfToken,
      () => {
        setSession(null);
        setStatus('login');
        setNotice('');
        setError({ summary: 'Phiên đăng nhập đã kết thúc. Vui lòng đăng nhập lại.' });
      },
    );
    return () => {
      configureAuthTransport(undefined, undefined);
    };
  }, [session]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const state = await getAuthStatus();
        if (!live) return;
        if (state.setupRequired) {
          setStatus('setup');
          return;
        }
        try {
          const current = await getCurrentSession();
          if (live) {
            setSession(current);
            setStatus('ready');
          }
        } catch {
          if (live) setStatus('login');
        }
      } catch (e) {
        if (live) {
          setError(operationIssue(e, 'Chưa thể kết nối hệ thống. Kiểm tra kết nối rồi thử lại.'));
          setStatus('login');
        }
      }
    })();
    return () => {
      live = false;
    };
  }, []);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setNotice('');
    const form = event.currentTarget;
    const constraint = firstConstraintIssue(form);
    if (constraint) {
      setError(constraint);
      return;
    }
    const data = new FormData(form);
    if (status === 'setup') {
      const issue = passwordIssue(data.get('password'), 'auth-password');
      if (issue) {
        setError(issue);
        return;
      }
    }
    setBusy(true);
    try {
      const credentials = {
        username: String(data.get('username') || ''),
        password:
          status === 'setup'
            ? validateNewPassword(data.get('password'))
            : String(data.get('password') || ''),
      };
      const current =
        status === 'setup'
          ? await setupAdmin({ ...credentials, displayName: String(data.get('displayName') || '') })
          : await logIn(credentials);
      setSession(current);
      setStatus('ready');
    } catch (e) {
      setError(
        operationIssue(
          e,
          status === 'setup'
            ? 'Chưa tạo được tài khoản quản trị. Kiểm tra thông tin rồi thử lại.'
            : 'Không thể đăng nhập. Kiểm tra tài khoản, mật khẩu và kết nối rồi thử lại.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }
  function endSession(message?: string) {
    setError(null);
    setNotice(message || '');
    setSession(null);
    setStatus('login');
  }
  async function logout() {
    try {
      await logOut();
    } catch {
      // Expired sessions still return safely to the sign-in screen.
    } finally {
      setSession(null);
      setStatus('login');
    }
  }
  if ((status === 'ready' && session) || (!requireLogin && status !== 'loading'))
    return (
      <AuthContext.Provider value={{ user: session?.user ?? null, logout, endSession }}>
        <SessionQueryProvider key={session?.csrfToken ?? 'public-view'}>
          {children}
        </SessionQueryProvider>
      </AuthContext.Provider>
    );
  if (status === 'loading' && loadingFallback) return <>{loadingFallback}</>;
  if (status === 'loading')
    return (
      <main>
        <LoadingSkeleton variant="page" label="Đang khởi tạo phiên làm việc…" />
      </main>
    );
  return (
    <main className="auth-screen">
      <section className="auth-card" aria-labelledby="sign-in-title">
        <a className="auth-brand" href="/" title={PRODUCT_DESCRIPTION} aria-label={PRODUCT_BRAND}>
          {PRODUCT_WORDMARK}
          <span>{PRODUCT_REGION}</span>
        </a>
        <h1 id="sign-in-title">
          {status === 'setup' ? 'Tạo quản trị viên đầu tiên' : 'Đăng nhập'}
        </h1>
        {
          <form
            noValidate
            onSubmit={submit}
            aria-busy={busy}
            aria-describedby={error ? 'auth-submit-error' : undefined}
          >
            <p>
              {status === 'setup'
                ? 'Thiết lập tài khoản quản trị để bắt đầu sử dụng hệ thống.'
                : 'Đăng nhập để truy cập dữ liệu dự án.'}
            </p>
            <label>
              Tên đăng nhập
              <input
                id="auth-username"
                name="username"
                data-field-label="Tên đăng nhập"
                aria-invalid={error?.field === 'auth-username' || undefined}
                required
                autoComplete="username"
                maxLength={128}
                minLength={status === 'setup' ? 3 : undefined}
                pattern={status === 'setup' ? USERNAME_PATTERN : undefined}
                aria-describedby={errorDescription(
                  status === 'setup' ? 'setup-username-help' : undefined,
                  error,
                  'auth-submit-error',
                  'auth-username',
                )}
                disabled={busy}
                autoCapitalize="none"
                spellCheck={false}
              />
            </label>
            {status === 'setup' && (
              <small id="setup-username-help">
                Tên đăng nhập từ 3 ký tự: chữ không dấu, số, dấu chấm, gạch ngang hoặc gạch dưới.
              </small>
            )}
            {status === 'setup' && (
              <label>
                Tên hiển thị
                <input
                  id="auth-display-name"
                  name="displayName"
                  data-field-label="Tên hiển thị"
                  aria-invalid={error?.field === 'auth-display-name' || undefined}
                  aria-describedby={errorDescription(
                    undefined,
                    error,
                    'auth-submit-error',
                    'auth-display-name',
                  )}
                  required
                  autoComplete="name"
                  maxLength={200}
                  disabled={busy}
                />
              </label>
            )}
            <label>
              Mật khẩu
              <input
                id="auth-password"
                name="password"
                data-field-label="Mật khẩu"
                aria-invalid={error?.field === 'auth-password' || undefined}
                type={showPassword ? 'text' : 'password'}
                required
                disabled={busy}
                aria-describedby={errorDescription(
                  status === 'setup' ? 'setup-password-help' : undefined,
                  error,
                  'auth-submit-error',
                  'auth-password',
                )}
                autoComplete={status === 'setup' ? 'new-password' : 'current-password'}
              />
            </label>
            {status === 'setup' && (
              <small id="setup-password-help">
                Khuyến nghị dùng từ 12 ký tự, kết hợp chữ, số hoặc ký hiệu.
              </small>
            )}
            <label className="account-checkbox">
              <input
                type="checkbox"
                checked={showPassword}
                disabled={busy}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
              Hiện mật khẩu
            </label>
            <button className="button primary full" disabled={busy}>
              {busy ? 'Đang xử lý…' : status === 'setup' ? 'Tạo tài khoản quản trị' : 'Đăng nhập'}
            </button>
          </form>
        }
        {notice && (
          <p className="admin-notice" role="status">
            {notice}
          </p>
        )}
        <FormErrorSummary issue={error} id="auth-submit-error" />
      </section>
    </main>
  );
}
